import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthChange } from '../services/authService';
import { getUser } from '../services/userService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const triedNativeSync = React.useRef(false);

  useEffect(() => {
    const unsubscribe = onAuthChange(async (user) => {
      if (user) {
        setCurrentUser(user);
        try {
          const data = await getUser(user.uid);
          setUserData(data);
        } catch (e) {
          console.error("Error fetching user data:", e);
        }
        setLoading(false);
      } else if (!triedNativeSync.current) {
        triedNativeSync.current = true;
        // Web SDK has no user - try syncing from native auth
        try {
          const { Capacitor } = await import("@capacitor/core");
          if (Capacitor.isNativePlatform()) {
            const { FirebaseAuthentication } = await import("@capacitor-firebase/authentication");
            const nativeResult = await FirebaseAuthentication.getCurrentUser();
            if (nativeResult?.user) {
              // Get ID token and exchange for custom token to sign in web SDK
              const idTokenResult = await FirebaseAuthentication.getIdToken({ forceRefresh: true });
              if (idTokenResult?.token) {
                const response = await fetch("https://us-central1-dresswork-44979.cloudfunctions.net/createCustomTokenFromIdToken", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    "Authorization": "Bearer " + idTokenResult.token,
                  },
                  body: JSON.stringify({ uid: nativeResult.user.uid }),
                });
                const data = await response.json();
                if (data.token) {
                  const { signInWithCustomToken } = await import("firebase/auth");
                  const { auth } = await import("../config/firebase");
                  await signInWithCustomToken(auth, data.token);
                  // onAuthChange will fire again with the user
                  return;
                }
              }
            }
          }
        } catch (e) {
          console.log("Native sync failed:", e);
        }
        setCurrentUser(null);
        setUserData(null);
        setLoading(false);
      } else {
        setCurrentUser(null);
        setUserData(null);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const value = { currentUser, userData, setUserData, loading };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
